// ----------------------------------------------------------------------------
// Component: Order Sheet
// ----------------------------------------------------------------------------
//
// ----------------------------------------------------------------------------

// -------------------------------------
// Dependencies
// -------------------------------------

import './_order-sheet.scss';

// -------------------------------------
//  Variables
// -------------------------------------
const ORDER_SHEET_WRAPPER             = '.order-sheet-wrapper';
const QUANTITY_SWITCHER_COMPONENT     = 'quantity-switcher';
const ERROR_MESSAGE                   = '.order-sheet__error-message';
const VARIANT_TOTAL                   = '.order-sheet__variant-total';
const ORDER_TOTAL                     = '.order-sheet__product-total';
const LOADING_ANIMATION               = '<div class="loading-icon-sh"></div>';

// -------------------------------------
//  Order Sheet
// -------------------------------------
class orderSheet extends HTMLElement {
    constructor() {
        super();
        this.sectionId          = this.id;
        this.sectionWrapper     = this.closest(ORDER_SHEET_WRAPPER);
        this.quantitySwitchers  = this.querySelectorAll(QUANTITY_SWITCHER_COMPONENT);
        this.orderTotal         = this.querySelector(ORDER_TOTAL);
        this.errorMessage       = this.querySelector(ERROR_MESSAGE);
        this.inputTimer         = null;
        this.timeout            = null;

        this.quantitySwitchers.forEach((qtySwitch) => {
            qtySwitch.addEventListener('quantityChange', this.updateCart.bind(this));
        });
    }

    /**
     * Evaluates what type of cart API endpoint to use.
    */
    updateCart(event) {
        // Debounce input to prevent multiple requests
        clearTimeout(this.inputTimer);

        this.inputTimer = setTimeout(() => {
            const variantData = {
                'id': parseInt(event.detail.variantId, 10),
                'quantity': event.detail.qty
            };

            // Make a request to fetch the cart data
            fetch(window.Shopify.routes.root + 'cart.js')
            .then(response => {
                    if (!response.ok) {
                        throw new Error('Network response was not ok');
                    }

                    return response.json();
            })
            .then(response => {
                    if (response.message) {
                        return this.showError(response);
                    }

                    const lineItems = response.items;
                    // FYI: Cart API returns variant ID as a interger.
                    const existingLineItem = lineItems.find(item => item.variant_id == variantData.id);

                    // Show Loading Icon
                    const variantTotal          = this.querySelector(VARIANT_TOTAL + `[data-variant-id="${variantData.id}"]`);
                    variantTotal.innerHTML      = LOADING_ANIMATION;

                    if (existingLineItem) {
                        // Pass the line item key for cart/change.js
                        const changeData = {
                            id: existingLineItem.key,
                            quantity: variantData.quantity
                        };
                        this.changeQuantity(changeData);
                    } else {
                        this.addQuantity(variantData);
                    }
            })
            .catch(error => {
                    console.error('Error fetching cart data:', error);
            });
        }, 750); // Adjust input delay
    }

    /**
     * Add variant to cart if there were no line items with the requested variant ID.
    */
    addQuantity(variantData) {
        const itemsData = {items: [variantData]};

        fetch(window.Shopify.routes.root + 'cart/add.js', {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json'
            },
            body: JSON.stringify(itemsData)
          })
          .then(response => response.json())
          .then(response => {
            if (response.message) {
                return this.showError(response);
            }

            this.updateSection();
          })
          .catch((error) => {
            console.error('Error:', error);
          });
    }

    /**
     * Update variant qty if there was already a line item with the requested variant ID.
    */
    changeQuantity(variantData) {
        console.log('changeQuantity called with:', variantData);

        fetch(window.Shopify.routes.root + 'cart/change.js', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json'
            },
            body: JSON.stringify(variantData)
          })
          .then(response => response.json())
          .then(response => {
            console.log('changeQuantity response:', response);

            if (response.message) {
                return this.showError(response);
            }

            this.updateSection();
          })
          .catch((error) => {
            console.error('Error:', error);
          });
    }

    /**
     * Re-render order-sheet section when a change is made.
    */
    updateSection() {
        const sectionId = this.sectionId;
        const isCollectionPage = this.dataset.collectionPage === 'true';

        // For collection pages, just reload the page to refresh cart quantities
        if (isCollectionPage) {
            window.location.reload();
            return;
        }

        fetch(window.location.pathname + `?sections=${sectionId}`)
          .then(response => response.json())
          .then(response => {
            if (response.message) {
                return this.showError(response);
            }

            const sectionHTML = response;
            this.sectionWrapper.innerHTML = sectionHTML[`${sectionId}`];
            Shopify.miniCart().init();
          })
          .catch((error) => {
            console.error('Error:', error);
          });
    }

    showError(errorMessage) {
        const errorContent = errorMessage?.description ? errorMessage.description : errorMessage.message;
        this.errorMessage.innerHTML = errorContent;

        if (this.timeout) clearTimeout(this.timeout);

        // Don't auto-refresh on rate limit errors - let user try again manually
        if (errorMessage.status === 'too_many_requests' || errorMessage.status === 429) {
            this.timeout = setTimeout(()=> {
                this.errorMessage.style.transition = 'opacity .5s ease-out';
                this.errorMessage.style.opacity = '0';
            }, 10000); // Show for 10 seconds
            return;
        }

        this.timeout = setTimeout(()=> {
            this.errorMessage.style.transition = 'opacity .5s ease-out';
            this.errorMessage.style.opacity = '0';
        }, 3500);

        this.timeout = setTimeout(()=> {
            this.updateSection();
        }, 4000);
    }
}

customElements.define('order-sheet', orderSheet);

// -------------------------------------
//  Quantity Switcher
// -------------------------------------
class quantitySwitcher extends HTMLElement {
    constructor() {
        super();
        this.quantityContainer = this.querySelector('.order-sheet__quantity-container');
        this.minusBtn          = this.querySelector('.order-sheet__quantity-button--down');
        this.plusBtn           = this.querySelector('.order-sheet__quantity-button--up');
        this.inputBox          = this.querySelector('.order-sheet__quantity-input');

        this.updateButtonStates();

        this.quantityContainer.addEventListener('click', this.handleButtonClick.bind(this));
        this.inputBox.addEventListener('input', this.handleQuantityChange.bind(this));
    }

    updateButtonStates() {
        const value = parseInt(this.inputBox.value);
        this.minusBtn.disabled = value < 1;
    }

    handleButtonClick(event) {
        if (event.target.isSameNode(this.minusBtn)) {
            this.decreaseValue();
        } else if (event.target.isSameNode(this.plusBtn)) {
            this.increaseValue();
        }
    }

    decreaseValue() {
        let value = parseInt(this.inputBox.value);
        value = isNaN(value) ? 1 : Math.max(value - 1, 0);
        this.inputBox.value = value;
        this.updateButtonStates();
        this.handleQuantityChange();
    }

    increaseValue() {
        let value = parseInt(this.inputBox.value);
        value = isNaN(value) ? 1 : (value + 1);
        this.inputBox.value = value;
        this.updateButtonStates();
        this.handleQuantityChange();
    }

    handleQuantityChange() {
        let value = parseInt(this.inputBox.value);
        value = isNaN(value) ? 1 : value;

        const quantityChange = new CustomEvent('quantityChange', {
            detail: {
              qty: value,
              variantId: this.inputBox.dataset.variantId
            },
        });

        this.dispatchEvent(quantityChange);
    }
}

customElements.define('quantity-switcher', quantitySwitcher);
